import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { handleRequest } from '../../../utils/httpRequest';
import { getEndpointUrl } from '../../../utils/endPoints';
import DealerSearch from './DealerSearch/DealerSearch';
import DealerMap from './DealerMap/DealerMap';
import generalController from '../../../controllers/generalController';
import { capitalCities, localStorageKeys } from '../../../utils/constants';
import formValidationController from '../../../controllers/formValidationController';
import { convertPostalToCoordinates, getCurrentBounds, getDealersInViewport, getPreviousUserlocation, getUserGeoLocation, storeUserLocation } from '../../../utils/googleMapHelper';
import { languageController } from '../../../controllers/language/languageController';
import parse from 'html-react-parser';
import { getItemOnLocalStorage, pushToGTMDataLayer, titleCase } from '../../../utils/helpers';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Button from '../../1_Atoms/Button/Button';
import { Svg } from '../../1_Atoms/Svg/Svg';
import Modal from '../../2_Molecules/Modal/Modal';

function FindADealer({
  isEVonly,
  isStepRenderType,
  isEmbeddedOnBnP,
  onSelectDealer,
  formExtraParams,
  preDefinedUserLocation,
  preDefinedDealerId,
  dealerId,
  language,
  displayServiceHoursOnly,
  isModelPreOrder = false
}) {
  const [dealers, setDealers] = useState([])
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [map, setMap] = useState(null)
  const [statusMessage, setStatusMessage] = useState({ text: '', isError: false })
  const [errorMessage, setErrorMessage] = useState('')
  const [initPostalKeyword, setInitPostalKeyword] = useState(null)

  const [modalOpen, setIsModalOpen] = useState(false)
  const [dealerData, setDealerData] = useState(false)

  const componentRef = useRef(null);

  const { introductionText, BaSALink, BaTDLink, GaQLink } = formExtraParams

  const [searchType, setSearchType] = useState('')

	let res = [];

  const searchDealers = async (searchValue, searchType) => {
    if (!searchValue) return;

    setSearchType(searchType)

    if (errorMessage) {
      setErrorMessage('')
    }

    let dealerApi = ''
    let newBounds = null

    //retailer
    if(searchType=== 'retailer'){
      dealerApi = `${getEndpointUrl('get-dealer')}/${dealerId}`
    // For keyword search (dealer name or city name)
    }else if (searchType === 'keyword') {
      dealerApi = `${getEndpointUrl('find-dealers')}&keyword=${encodeURI(searchValue)}`;
    }
    // For geolocation search (lat and long)
    else if (searchType === 'geolocation') {
      // Set the passed coordinates value as center.
      map.setCenter({
        lat: searchValue.lat,
        lng: searchValue.lng
      })
      // Get new bounds after setCenter
      newBounds = getCurrentBounds(map)
      // Get a dealer api url with some params.
      // Pass 'newBounds' to api to limit dealers based on min/max lat and long.
      dealerApi = `${getEndpointUrl('find-dealers')}&lat=${searchValue.lat}&lng=${searchValue.lng}&minlng=${newBounds.minLong}&maxlng=${newBounds.maxLong}&minlat=${newBounds.minLat}&maxlat=${newBounds.maxLat}`;
    } else if (searchType === 'init') {
        map.setCenter({
          lat: 58.6348024,
          lng: -102.6680236
        })
        // Get new bounds after setCenter
        newBounds = getCurrentBounds(map)
    }

    if (isEVonly) {
      if(searchType==='retailer'){
        dealerApi = `${dealerApi}?ev_only=true`;
      }else{
        dealerApi = `${dealerApi}&ev_only=true`;
      }
    }

    try {
      const res = await handleRequest('get', dealerApi);
      let dealerData;
      // check if retailer result
      if(res.data.dealer_code){
        dealerData = [res.data];
      }else{
        dealerData = res.data;
      }
      // Check if res includes dealers data.
      if (dealerData.length > 0) {
        if (searchType === 'keyword') {
          // Sort dealers alphabetically
          //dealerData.sort((a, b) => (a.name > b.name) ? 1 : -1);
          
          // Set the first most relavant dealer as center.
          map.setCenter({
            lat: dealerData[0].geolocation.latitude,
            lng: dealerData[0].geolocation.longitude
          })
          
          // Get new bounds after setCenter
          newBounds = getCurrentBounds(map)
        } 

        const preferredDealer = window.mzFunc.checkCookie('preferredDealer');
        const index = dealerData.findIndex(
          dealer => dealer.dealer_code === preferredDealer
        );

        if (index > 0) {
          // Remove the dealer from its current position
          const [preferredDealer] = dealerData.splice(index, 1);
          // Add it to the front of the array
          dealerData.unshift(preferredDealer);
        }

        setDealers(dealerData)
        
        window.mzCollapseBlock.init(document.querySelectorAll('.mzReactForms .mz-collapse-forms'))
      } else {
        setErrorMessage(languageController.getCopy('findDealer', 'errorNoResults'))
      }
    } catch (err) {
      //console.log(err)
      //setErrorMessage(languageController.getCopy('findDealer', 'errorNoResults'))
      setDealers([])
    }
  }

  const geoResults = async (res)=>{
	  if (res.success === true) {
	   const geoDealer = await searchDealers(res.data, 'geolocation')
		setStatusMessage({ isError: false, text: '' })
		// Set user geolocation in localstorage.
		storeUserLocation('geolocation', res.data)
		pushToGTMDataLayer({ event: 'Dealer search by current location', result: 'success', location: res.data });
	}else if(res.success === 'timeout'){
		setStatusMessage({ text: languageController.getCopy('findDealer', 'geoUnavailable'), isError: true });
		setTimeout(() => {
		  setStatusMessage({ isError: false, text: '' })
		}, 3000)
	  } else {
		setStatusMessage({ text: res.data, isError: true })
		pushToGTMDataLayer({ event: 'Dealer search by current location', result: 'error', location: null });
		// Remove error message after 3s
		setTimeout(() => {
		  setStatusMessage({ isError: false, text: '' })
		}, 3000)
	  }
  }

  // Search dealers based on entered search value.
  const onDealerSearchSubmit = async (dealerValue, postalValue) => {
    if (!dealerValue && !postalValue) return;

    const searchValue = dealerValue.length > 0 ? dealerValue : postalValue;

    setStatusMessage({ text: languageController.getCopy('findDealer', 'searching'), isError: false })

    // Check if it is a postalcode search
    if (formValidationController.validateField(searchValue, 'postalcode')) {
      const coordinates = await convertPostalToCoordinates(searchValue)
      await searchDealers(coordinates, 'geolocation')
      // Store user postalcode data in localstorage
      storeUserLocation('postalcode', { postalcode: searchValue, coordinates: coordinates })
    }
    // Otherwise, perform a keyword search
    else {
      await searchDealers(searchValue, 'keyword')
    }
    setStatusMessage({ isError: false, text: '' })
  }

  // Search dealers based on user's geolocation.
  const onGeolocationSubmit = async () => {
    const locData = await new Promise( async function(resolve, reject) {
        //setTimeout(() => reject(), 10000);
        let geoCount=0;
        const checkGeoPermission = setInterval(()=>{
          if(isGeoPermitted === 'denied'){
            reject();
          }else if(isGeoPermitted=== 'prompt'){
            geoCount++;
            if(geoCount>13){
              reject();
            }
          }else if(isGeoPermitted === 'granted'){
            clearInterval(checkGeoPermission);
          }
        }, 1000)
        res = await getUserGeoLocation()
        resolve();
    }).then(
      (result) => {
        geoResults(res);
      },
      (error) =>{
        res.success = 'timeout';
        geoResults(res);
      }
    );
  }

  function isGeoPermitted(){
    navigator.permissions.query({name:'geolocation'}).then(function(result) {
      // Will return ['granted', 'prompt', 'denied']
      //console.log(result.state);
      return result.state;
    });
  }

  useEffect(() => {
    // Load dealers based on what info is available after map gets loaded.
    async function handleInitLoad() {
      setStatusMessage({ text: languageController.getCopy('findDealer', 'searching'), isError: false })
      // Get previously set dealer data if exists.
      const prevDealer = getItemOnLocalStorage(localStorageKeys.DEALER_SELECTED_BY_USER);
      // Get previously set user location data if exists.
      const prevUserLocationData = getPreviousUserlocation();

      if(typeof window.mzFunc.checkCookie === 'function'){
        if(window.mzFunc.checkCookie('preferredDealer') !== ''){
          if(!dealerId) dealerId = window.mzFunc.checkCookie('preferredDealer')
        }
      }
      //realailer location
      if(dealerId){
        await searchDealers(dealerId, 'retailer')
      // If it is a step render type AND userLocation is already defined in URL Params.
      }else if (isStepRenderType && preDefinedUserLocation) {
        await searchDealers({ lat: preDefinedUserLocation.lat, lng: preDefinedUserLocation.lng }, 'geolocation')
      }
      // If it is a step render type AND dealer was chosen before on the site.
      else if (prevDealer) {
        await searchDealers({ lat: prevDealer.geolocation.latitude, lng: prevDealer.geolocation.longitude }, 'geolocation')
      }
      // If user location already exists in localstorage (geolocation or postalcode)
      else if (prevUserLocationData) {
        // Geolocation
        if (prevUserLocationData.type === 'geolocation') {
          await searchDealers(prevUserLocationData.value, 'geolocation')
        }
        // Postalcode
        else if (prevUserLocationData.type === 'postalcode') {
          const { coordinates, postalcode } = prevUserLocationData.value;
          await searchDealers(coordinates, 'geolocation')
          // Set postalcode as an init input value.
          setInitPostalKeyword(postalcode)
        }
      }
      // Otherwise, use capital city of user's province
      else {
        const capitalData = capitalCities[generalController.getMetatagValue('province')];
        if (capitalData) {
           await searchDealers(capitalData.location, 'init')
        }
      }

      setStatusMessage({ isError: false, text: '' })
    }

    if (map) {
      ScrollTrigger.create({
        trigger: componentRef.current,
        once: true,
        start: "-200% top",
        onEnter: () => {
          handleInitLoad();
        },
      });
    }
  }, [map, dealerId, preDefinedDealerId])

  return (
    <div className="mz-find-a-dealer" data-mz-form-type="FindADealer" ref={componentRef}>
      {introductionText && (
        <div className="mz-find-a-dealer__intro-text mz-container">
          <div className="mz-full-width">
            {parse(introductionText)}
          </div>
        </div>
      )}
      {!window.bnp_retailer_mode &&
          <DealerSearch
            // initDealerKeyword={initDealerKeyword}
            initPostalKeyword={initPostalKeyword}
            onSubmit={onDealerSearchSubmit}
            onGeolocationSubmit={() => onGeolocationSubmit()}
            statusMessage={statusMessage}
            errorMessage={errorMessage}
          />
      }
      <DealerMap
        dealers={dealers}
        searchDealers={searchDealers}
        isMapLoaded={isMapLoaded}
        setMap={setMap}
        map={map}
        setIsMapLoaded={setIsMapLoaded}
        isStepRenderType={isStepRenderType}
        isEmbeddedOnBnP={isEmbeddedOnBnP}
        onSelectDealer={onSelectDealer}
        BaSALink={BaSALink}
        BaTDLink={BaTDLink}
        GaQLink={GaQLink}
        currentLang={generalController.detectLanguage()}
        preDefinedDealerId={preDefinedDealerId}
        language={language}
        displayServiceHoursOnly={displayServiceHoursOnly}
        setIsModalOpen={setIsModalOpen}
        setDealerData={setDealerData}
        isModelPreOrder={isModelPreOrder}
        searchType={searchType}
      />
      <Modal
          className="mz-modal--non-sticky-top mz-modal-fifty-fifty"
          isOpen={modalOpen}
          onClose={() => {
            document.querySelector('body').classList.remove('disabled')
            document.querySelector('.mz-react-forms-root').style.zIndex=1;
            setIsModalOpen(false);
            handleBodyScrollHidden(false);
          }}
          children={(
            <div className="mz-modal__text-container">
                <h3 className="text--center">{languageController.getCopy('findDealer', 'modalServiceTitle')}</h3>
                <div className="mz-spacer mz-spacer-s"></div>
                <p className="text--center">{languageController.getCopy('findDealer', 'modalServiceContent').replace('|dealer|', titleCase(dealerData.name))}</p>
                <div className="mz-spacer mz-spacer-s"></div>
                <div className="mz-container-flex">
                <Button
                      label={languageController.getCopy('findDealer', 'modalServiceButton').replace('|dealer|', dealerData.name)}
                      className="mz-btn--red"
                      onClick={(e) => {
                        document.querySelector('.mz-react-forms-root').style.zIndex=1;
                        document.querySelector('body').classList.remove('disabled')
                        setIsModalOpen(false);
                        document.querySelector('body').classList.remove('disabled');
                        window.open(dealerData.schedule_service_link, '_blank').focus();
                        //window.location.href=dealerData.schedule_service_link;
                    }}
                      data-menu-name="Select dealer"
                      data-form-name="Find a dealer"
                      data-form-event="Book Service"
                    />
                </div>
            </div>
          )}
        />
    </div>
  )
}

FindADealer.propTypes = {
  isEVonly: PropTypes.bool,
  isStepRenderType: PropTypes.bool,
  isEmbeddedOnBnP: PropTypes.bool,
  onSelectDealer: PropTypes.func,
  formExtraParams: PropTypes.object,
  preDefinedUserLocation: PropTypes.object,
  preDefinedDealerId: PropTypes.string,
  language: PropTypes.string,
}

FindADealer.defaultProps = {
  isEVonly: false,
  isStepRenderType: false,
  isEmbeddedOnBnP: false,
  onSelectDealer: undefined,
  formExtraParams: {}
}

export default FindADealer